import dayjs from 'dayjs';

import { formatDateString, formatDayTimeString } from '~libs/dayjs';

import {
  checkboxEditor,
  currencyEditor,
  dateEditor,
  multiSelectEditor,
  numberEditor,
  selectEditor,
  textAreaEditor,
  textEditor,
} from './columnEditors';
import { checkboxCellRender, currencyCellRender, genericCellRender, linkCellRender } from './columnRenderers';
import type { Column } from './types';

export function getDefaultRenderReadOnly(type?: string, column?: Column) {
  switch (type) {
    case 'checkbox':
      return checkboxCellRender(column);
    case 'currency':
      return currencyCellRender(column?.id || '', column?.componentProps?.noDecimals ?? false, undefined, column);
    case 'link':
      return linkCellRender(
        () => {
          /* no-op */
        },
        undefined,
        column,
      );
    case 'number':
      return genericCellRender((v) => (v != null ? v.toString() : ''), undefined, column);
    case 'date':
      return genericCellRender(
        (value) => (dayjs(value).isValid() ? formatDateString(value) : 'N/A'),
        undefined,
        column,
      );
    case 'datetime':
      return genericCellRender(
        (value) => (dayjs(value).isValid() ? formatDayTimeString(value) : 'N/A'),
        undefined,
        column,
      );
    case 'select':
      return genericCellRender(
        (v) => {
          const opt = column?.options?.find((o) => o.id === v);
          return opt?.name ?? '';
        },
        undefined,
        column,
      );
    case 'multi-lookup':
      return genericCellRender(
        (v) => {
          const opts = column?.options?.filter(({ id }) => v?.includes(id));
          return opts?.map(({ name }) => name).join(', ') ?? '';
        },
        undefined,
        column,
      );
    default:
      return genericCellRender((v) => v ?? '', undefined, column);
  }
}

export function getDefaultRenderEditor(type?: string, column?: Column) {
  switch (type) {
    case 'checkbox':
      return checkboxEditor(column);
    case 'currency':
      return currencyEditor(column?.id || '', false, column);
    case 'number':
      return numberEditor(column?.id || '', 0, undefined, column?.componentProps?.noDecimals ?? false, column);
    case 'date':
      return dateEditor(column?.id || '', undefined, undefined, column);
    case 'select':
      return selectEditor(column?.options || [], undefined, column);
    case 'multi-lookup':
      return multiSelectEditor(column?.options || [], undefined, column);
    case 'text':
      return textEditor(column?.id || '', column);
    case 'textarea':
      return textAreaEditor(column?.id || '', column);
    default:
      return undefined;
  }
}
